import React, { useState, useEffect, useRef } from 'react';

const RadarScanner = ({
  isActive = true,
  hosts = [],
  ports = 0,
  vulnerabilities = [],
  exploits = 0
}) => {
  const [detections, setDetections] = useState([]);
  const [radarAngle, setRadarAngle] = useState(0);
  const prevCounts = useRef({ hosts: 0, ports: 0, vulns: 0, exploits: 0 });

  // Radar rotation animation
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setRadarAngle(prev => (prev + 2) % 360);
    }, 30);

    return () => clearInterval(interval);
  }, [isActive]);

  // Detect new items and add blips
  useEffect(() => {
    const newDetections = [];
    const currentCounts = {
      hosts: hosts.length,
      ports: ports,
      vulns: vulnerabilities.length,
      exploits: exploits
    };

    // New hosts detected
    if (currentCounts.hosts > prevCounts.current.hosts) {
      const diff = currentCounts.hosts - prevCounts.current.hosts;
      for (let i = 0; i < diff; i++) {
        newDetections.push({
          id: `host-${Date.now()}-${i}`,
          type: 'host',
          angle: Math.random() * 360,
          distance: 0.3 + Math.random() * 0.5,
          timestamp: Date.now()
        });
      }
    }

    // New ports detected
    if (currentCounts.ports > prevCounts.current.ports) {
      const diff = Math.min(currentCounts.ports - prevCounts.current.ports, 10);
      for (let i = 0; i < diff; i++) {
        newDetections.push({
          id: `port-${Date.now()}-${i}`,
          type: 'port',
          angle: Math.random() * 360,
          distance: 0.2 + Math.random() * 0.6,
          timestamp: Date.now()
        });
      }
    }

    // New vulnerabilities detected
    if (currentCounts.vulns > prevCounts.current.vulns) {
      const newVulns = vulnerabilities.slice(prevCounts.current.vulns);
      newVulns.forEach((vuln, i) => {
        newDetections.push({
          id: `vuln-${Date.now()}-${i}`,
          type: vuln.severity === 'critical' ? 'critical' : vuln.severity === 'high' ? 'high' : 'vuln',
          angle: Math.random() * 360,
          distance: 0.4 + Math.random() * 0.4,
          timestamp: Date.now()
        });
      });
    }

    // New exploits detected
    if (currentCounts.exploits > prevCounts.current.exploits) {
      const diff = currentCounts.exploits - prevCounts.current.exploits;
      for (let i = 0; i < diff; i++) {
        newDetections.push({
          id: `exploit-${Date.now()}-${i}`,
          type: 'exploit',
          angle: Math.random() * 360,
          distance: 0.5 + Math.random() * 0.35,
          timestamp: Date.now()
        });
      }
    }

    if (newDetections.length > 0) {
      setDetections(prev => [...prev, ...newDetections].slice(-100)); // Keep last 100
    }

    prevCounts.current = currentCounts;
  }, [hosts.length, ports, vulnerabilities.length, exploits]);

  // Fade out old detections
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setDetections(prev => prev.filter(d => now - d.timestamp < 30000)); // 30 sec fade
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getBlipColor = (type) => {
    switch (type) {
      case 'host': return '#22c55e'; // green
      case 'port': return '#3b82f6'; // blue
      case 'vuln': return '#f59e0b'; // amber
      case 'high': return '#f97316'; // orange
      case 'critical': return '#ef4444'; // red
      case 'exploit': return '#a855f7'; // purple
      default: return '#6b7280';
    }
  };

  const getBlipSize = (type) => {
    switch (type) {
      case 'critical':
      case 'exploit': return 6;
      case 'high': return 5;
      case 'vuln': return 4;
      case 'host': return 4;
      case 'port': return 3;
      default: return 3;
    }
  };

  return (
    <div className="relative flex flex-col items-center">
      {/* Radar Container */}
      <div className="relative w-64 h-64">
        {/* Outer ring with glow */}
        <div className="absolute inset-0 rounded-full border-2 border-brand-green/30 shadow-lg shadow-brand-green/20" />

        {/* Inner rings */}
        <div className="absolute inset-8 rounded-full border border-brand-green/20" />
        <div className="absolute inset-16 rounded-full border border-brand-green/15" />
        <div className="absolute inset-24 rounded-full border border-brand-green/10" />

        {/* Cross lines */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-px bg-brand-green/10" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-px h-full bg-brand-green/10" />
        </div>

        {/* Radar sweep */}
        {isActive && (
          <div
            className="absolute inset-0 origin-center"
            style={{ transform: `rotate(${radarAngle}deg)` }}
          >
            {/* Sweep gradient */}
            <div
              className="absolute top-1/2 left-1/2 w-1/2 h-1"
              style={{
                background: 'linear-gradient(90deg, rgba(34, 197, 94, 0.8), transparent)',
                transformOrigin: 'left center',
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.5)'
              }}
            />
            {/* Trailing fade */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(from ${radarAngle}deg, rgba(34, 197, 94, 0.15) 0deg, transparent 60deg, transparent 360deg)`,
              }}
            />
          </div>
        )}

        {/* Center dot */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`w-3 h-3 rounded-full bg-brand-green ${isActive ? 'animate-pulse' : ''}`} />
        </div>

        {/* Detection blips */}
        {detections.map((detection) => {
          const age = Date.now() - detection.timestamp;
          const opacity = Math.max(0.3, 1 - (age / 30000));
          const x = Math.cos((detection.angle * Math.PI) / 180) * detection.distance * 120;
          const y = Math.sin((detection.angle * Math.PI) / 180) * detection.distance * 120;
          const size = getBlipSize(detection.type);

          return (
            <div
              key={detection.id}
              className="absolute rounded-full transition-opacity"
              style={{
                width: size,
                height: size,
                backgroundColor: getBlipColor(detection.type),
                boxShadow: `0 0 ${size * 2}px ${getBlipColor(detection.type)}`,
                left: `calc(50% + ${x}px - ${size / 2}px)`,
                top: `calc(50% + ${y}px - ${size / 2}px)`,
                opacity: opacity,
              }}
            />
          );
        })}

        {/* Scan status overlay */}
        {!isActive && (
          <div className="absolute inset-0 rounded-full bg-gray-900/50 flex items-center justify-center">
            <span className="text-gray-400 text-sm font-medium">SCAN TERMINÉ</span>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm shadow-green-500/50" />
          <span className="text-gray-400">Hôtes</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
          <span className="text-gray-400">Ports</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50" />
          <span className="text-gray-400">CVE</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm shadow-red-500/50" />
          <span className="text-gray-400">Critical</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500 shadow-sm shadow-purple-500/50" />
          <span className="text-gray-400">Exploit</span>
        </div>
      </div>

      {/* Live counts under legend */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
        <div className="bg-gray-800/50 rounded px-2 py-1">
          <div className="text-green-400 font-bold">{hosts.length}</div>
          <div className="text-gray-500">IPs</div>
        </div>
        <div className="bg-gray-800/50 rounded px-2 py-1">
          <div className="text-blue-400 font-bold">{ports}</div>
          <div className="text-gray-500">Ports</div>
        </div>
        <div className="bg-gray-800/50 rounded px-2 py-1">
          <div className="text-amber-400 font-bold">{vulnerabilities.length}</div>
          <div className="text-gray-500">Vulns</div>
        </div>
        <div className="bg-gray-800/50 rounded px-2 py-1">
          <div className="text-purple-400 font-bold">{exploits}</div>
          <div className="text-gray-500">Exploits</div>
        </div>
      </div>
    </div>
  );
};

export default RadarScanner;
